import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Box, Paper, Fade, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { changePassword } from '../utils/api';
import { useCustomer } from '../contexts/customer-context';
import Header from './Header';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
  transition: 'all 0.3s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { customerId } = useCustomer();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await changePassword(customerId, currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Container maxWidth="xs" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Fade in={true} timeout={1000}>
          <StyledPaper elevation={3}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Change Password
            </Typography>
            <Box sx={{ mt: 1, width: '100%' }}>
              <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mt: 2 }}>Password changed successfully!</Alert>}
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  fullWidth 
                  disabled={loading}
                  sx={{ 
                    mt: 3, 
                    py: { xs: 1, sm: 1.5 }, 
                    fontWeight: 600, 
                    fontSize: { xs: '0.9rem', sm: '1rem' }, 
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
                    '&:hover': { 
                      transform: 'translateY(-2px)', 
                      boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)' 
                    } 
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Change Password'}
                </Button>
              </form>
            </Box>
          </StyledPaper>
        </Fade>
      </Container>
    </>
  );
};

export default ChangePassword;